import type { InstagramImageAdaptation } from '../useInstagramImageAdaptation';

import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Box,
  CloseButton,
  Field,
  Flex,
  Input,
  InputGroup,
  Text,
  Textarea,
} from '@valwin/facto/components';
import { FileUpIcon } from '@valwin/facto/icons';

import { useErrorTranslator } from '~shared/i18n';
import { ImageUploadCrop } from '~shared/ui';

import { useInstagramShareForm } from '../context';
import { type InstagramShareFormErrors, MAX_CAPTION_LENGTH } from '../types';

type InstagramShareFormProps = {
  imageAdaptation: InstagramImageAdaptation;
};

export function InstagramShareForm({
  imageAdaptation,
}: InstagramShareFormProps) {
  const { t } = useTranslation();

  const translateError = useErrorTranslator<InstagramShareFormErrors>({
    PHOTO_REQUIRED: () => t('errors imageRequired'),
  });

  const {
    register,
    control,
    trigger,
    formState: { errors },
  } = useInstagramShareForm();

  const caption = useWatch({ control, name: 'caption' });
  const captionLength = caption?.length ?? 0;

  const {
    status: imageStatus,
    adaptFile,
    reset: resetImageStatus,
  } = imageAdaptation;

  return (
    <Flex direction="column" gap="4">
      <Field.Root required invalid={!!errors.caption}>
        <Field.Label>
          {t('thread message')} <Field.RequiredIndicator />
        </Field.Label>
        <Textarea
          placeholder={t('integrations instagram share caption')}
          {...register('caption', { onChange: () => trigger('caption') })}
          rows={6}
          minHeight="75px"
          maxHeight="150px"
        />
        <Field.HelperText alignSelf="end">
          <Box as="span" color={errors.caption?.message ? 'red' : 'inherit'}>
            {captionLength}
          </Box>{' '}
          / {MAX_CAPTION_LENGTH}
        </Field.HelperText>
      </Field.Root>

      <Field.Root required invalid={!!errors.imageUrls}>
        <Field.Label>
          {t('richTextEditor image tooltip')} <Field.RequiredIndicator />
        </Field.Label>
        <Controller
          name="imageUrls"
          control={control}
          render={({ field }) => (
            <ImageUploadCrop.Root
              currentImage={field.value[0]}
              ratio={{ x: 1, y: 1 }}
              onCrop={adaptFile}
              onClear={() => {
                resetImageStatus();
                field.onChange([]);
              }}
            >
              <InputGroup
                startElement={<FileUpIcon size="sm" />}
                endElement={
                  <ImageUploadCrop.ClearTrigger>
                    <CloseButton
                      me="-1"
                      size="xs"
                      colorPalette="gray"
                      focusVisibleRing="inside"
                      focusRingWidth="2px"
                      pointerEvents="auto"
                    />
                  </ImageUploadCrop.ClearTrigger>
                }
              >
                <Input asChild cursor="pointer">
                  <ImageUploadCrop.Trigger>
                    <ImageUploadCrop.FileText
                      placeholder={t(
                        'integrations facebook share select image'
                      )}
                    />
                  </ImageUploadCrop.Trigger>
                </Input>
              </InputGroup>
              <ImageUploadCrop.Dialog />
            </ImageUploadCrop.Root>
          )}
        />
        {imageStatus === 'processing' && (
          <Field.HelperText>
            {t('integrations instagram share image adapting')}
          </Field.HelperText>
        )}
        {imageStatus === 'adapted' && (
          <Field.HelperText>
            {t('integrations instagram share image adapted')}
          </Field.HelperText>
        )}
        {imageStatus === 'notCroppable' && (
          <Text textStyle="sm" color="red">
            {t('integrations instagram share image notCroppable')}
          </Text>
        )}
        <Field.ErrorText>
          {errors.imageUrls?.message &&
            translateError(errors.imageUrls.message)}
        </Field.ErrorText>
      </Field.Root>
    </Flex>
  );
}
